import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ---------------------------------------------------------------------------
// Shared dot-indicator carousel.
//
// Non-looping by design: when the user is on the first slide there is no
// item visible to its left, and likewise for the last slide on the right.
// Arrow buttons clamp at the boundaries and the dots map 1:1 to the items.
// ---------------------------------------------------------------------------

export interface DotCarouselSlide {
  /** Stable per-item key. */
  key: string;
  node: React.ReactNode;
}

export interface DotCarouselProps {
  slides: DotCarouselSlide[];
  /**
   * Responsive flex-basis for each slide. Controls how much of the focused
   * card is visible and how big the side peeks are. Defaults work for the
   * Highlights & eBook page.
   */
  slideFlex?: { xs?: string; sm?: string; md?: string; lg?: string };
  /** Show desktop prev/next arrow buttons. Default: true. */
  showArrows?: boolean;
  /**
   * Vertical anchor for the prev/next arrows, expressed as a CSS
   * `top` value (e.g. `'50%'`, `'120px'`). The arrows are
   * `transform: translateY(-50%)` so this is the visual center,
   * not the top edge. Defaults to `'50%'` (slide center). Override
   * when the slide's cover image isn't vertically centered — e.g.
   * a `ProjectCard` whose image sits in the upper third of the
   * card needs `~25%` to keep the arrows on the photo rather than
   * floating over the action buttons below.
   */
  arrowTop?: string;
  /**
   * When true, the pagination dots are absolute-positioned over
   * the bottom of the slide (and recoloured for a dark backdrop)
   * instead of rendered as a separate row beneath the carousel.
   * Use this for hero-style slides where the dots should sit on
   * top of the image — e.g. the dashboard "Featured" carousel.
   * Defaults to false.
   */
  dotsOverlay?: boolean;
  /** Gap between slides (responsive, MUI spacing units). */
  gap?: { xs?: number; sm?: number };
  /**
   * On mobile/tablet, switch from 1:1 dots to a compact counter +
   * progress bar when the slide count exceeds this. Set to 0 to always
   * show dots. Defaults to 9.
   */
  compactDotsThreshold?: number;
}

const DEFAULT_SLIDE_FLEX: NonNullable<DotCarouselProps['slideFlex']> = {
  xs: '0 0 80%',
  sm: '0 0 64%',
  md: '0 0 52%',
  lg: '0 0 44%',
};

export default function DotCarousel({
  slides,
  slideFlex,
  showArrows = true,
  arrowTop = '50%',
  dotsOverlay = false,
  gap,
  compactDotsThreshold = 9,
}: DotCarouselProps) {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const trackRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const count = slides.length;
  const [activeIndex, setActiveIndex] = useState(0);
  // Suppresses `handleScroll`'s active-index override while a programmatic
  // smooth scroll (dot click / arrow / resize) is animating. Without this,
  // intermediate frames of the animation can flip `activeIndex` back to
  // whichever item is currently closest to viewport center, defeating the
  // explicit user choice — and for edge items that can't be centered, the
  // chosen item never wins the "closest" race so the dots appear stuck.
  const isProgrammaticRef = useRef(false);
  const programmaticTimerRef = useRef<number | null>(null);

  // Identify the slide whose center is closest to the viewport center —
  // works regardless of item width, viewport size, or scroll velocity.
  const computeClosestIdx = (): number => {
    const el = trackRef.current;
    if (!el) return activeIndex;
    const viewportCenter = el.scrollLeft + el.clientWidth / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    itemRefs.current.forEach((node, i) => {
      if (!node) return;
      const itemCenter = node.offsetLeft + node.offsetWidth / 2;
      const dist = Math.abs(itemCenter - viewportCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    });
    return bestIdx;
  };

  const goTo = (idx: number, behavior: ScrollBehavior = 'smooth') => {
    const clamped = Math.max(0, Math.min(count - 1, idx));
    // Commit the visual selection immediately so the dots, scale, and
    // opacity respond even when the track has no horizontal overflow
    // (e.g. EDM carousels where all small posters already fit on screen).
    setActiveIndex(clamped);
    const el = trackRef.current;
    const node = itemRefs.current[clamped];
    if (!el || !node) return;
    // Compute the scrollLeft that would put the target's center under the
    // track's viewport center, then clamp to the scrollable range. This
    // scrolls *only* the track — `scrollIntoView` would also scroll every
    // ancestor (page, dialog, etc.), which was making the carousel feel
    // "stuck" because the page was scrolling instead of the track.
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const desired = node.offsetLeft + node.offsetWidth / 2 - el.clientWidth / 2;
    const target = Math.max(0, Math.min(maxScroll, desired));
    // Mark the upcoming scroll events as programmatic so `handleScroll`
    // doesn't override the chosen `activeIndex` mid-animation.
    isProgrammaticRef.current = true;
    if (programmaticTimerRef.current !== null) {
      window.clearTimeout(programmaticTimerRef.current);
    }
    programmaticTimerRef.current = window.setTimeout(() => {
      isProgrammaticRef.current = false;
      programmaticTimerRef.current = null;
    }, behavior === 'smooth' ? 500 : 50);
    el.scrollTo({ left: target, behavior });
  };

  // Re-clamp the active slide when the viewport resizes (item widths change
  // with breakpoints, so a previously valid scroll position can drift).
  useEffect(() => {
    const onResize = () => goTo(activeIndex, 'auto');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeIndex]);

  const handleScroll = () => {
    // Ignore scroll events that are part of an in-flight programmatic
    // jump — `goTo` has already set the authoritative `activeIndex`.
    if (isProgrammaticRef.current) return;
    const idx = computeClosestIdx();
    if (idx !== activeIndex) setActiveIndex(idx);
  };

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  if (count === 0) return null;

  const flex = { ...DEFAULT_SLIDE_FLEX, ...(slideFlex ?? {}) };
  const useCompactPagination = true;

  const paginationRowSx = dotsOverlay
    ? {
        position: 'absolute' as const,
        left: 0,
        right: 0,
        bottom: { xs: 12, sm: 16 },
        zIndex: 2,
        pointerEvents: 'none' as const,
        '& > *': { pointerEvents: 'auto' as const },
      }
    : { mt: 1.25 };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Track */}
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          gap: { xs: gap?.xs ?? 1.25, sm: gap?.sm ?? 2 },
          overflowX: 'auto',
          overflowY: 'visible',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: 1,
        }}
      >
        {slides.map((slide, idx) => {
          const isActive = idx === activeIndex;
          return (
            <Box
              key={slide.key}
              ref={(el: HTMLDivElement | null) => {
                itemRefs.current[idx] = el;
              }}
              sx={{
                flex,
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                transition: 'transform 220ms ease, opacity 220ms ease',
                transform: isActive ? 'scale(1)' : 'scale(0.94)',
                opacity: 1,
                cursor: isActive ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (!isActive) goTo(idx);
              }}
            >
              {slide.node}
            </Box>
          );
        })}
      </Box>

      {/* Prev / next on >= sm — handy on desktop; dots are still primary. */}
      {showArrows && count > 1 && (
        <>
          <IconButton
            aria-label="Previous"
            size="small"
            onClick={goPrev}
            disabled={activeIndex === 0}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'absolute',
              top: arrowTop,
              transform: 'translateY(-50%)',
              left: 8,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              '&.Mui-disabled': { opacity: 0.3, color: '#fff' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label="Next"
            size="small"
            onClick={goNext}
            disabled={activeIndex === count - 1}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'absolute',
              top: arrowTop,
              transform: 'translateY(-50%)',
              right: 8,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              '&.Mui-disabled': { opacity: 0.3, color: '#fff' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Dots — one per item, mapped 1:1 to the active index.
          When `dotsOverlay` is set, the row is absolute-positioned
          over the bottom of the slide and uses a white-on-dark
          palette so it reads against a hero photo. Otherwise the
          dots sit in a normal flow row below the carousel. */}
      {count > 1 && (
        useCompactPagination ? (
          <Stack
            alignItems="center"
            spacing={0.75}
            sx={paginationRowSx}
            aria-label={`Slide ${activeIndex + 1} of ${count}`}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: dotsOverlay
                  ? 'rgba(255,255,255,0.92)'
                  : 'var(--color-text-muted)',
                textShadow: dotsOverlay
                  ? '0 1px 3px rgba(0,0,0,0.45)'
                  : 'none',
              }}
            >
              {activeIndex + 1} of {count}
            </Typography>
            <Box
              sx={{
                width: 'min(200px, 72%)',
                height: 4,
                borderRadius: 999,
                bgcolor: dotsOverlay
                  ? 'rgba(255,255,255,0.35)'
                  : 'var(--color-border-strong)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${((activeIndex + 1) / count) * 100}%`,
                  height: '100%',
                  borderRadius: 999,
                  bgcolor: dotsOverlay
                    ? 'rgba(255,255,255,0.95)'
                    : 'var(--color-accent)',
                  transition: 'width 220ms ease',
                }}
              />
            </Box>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="center"
            sx={paginationRowSx}
          >
            {slides.map((slide, idx) => {
              const active = idx === activeIndex;
              return (
                <Box
                  key={slide.key}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to slide ${idx + 1}`}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => goTo(idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goTo(idx);
                    }
                  }}
                  sx={{
                    // NB: numeric `width`/`height` in `sx` resolves through the
                    // theme spacing scale (8px units) — use explicit pixel
                    // strings so the dots render at their intended size.
                    width: active ? '24px' : '12px',
                    height: '12px',
                    borderRadius: 999,
                    bgcolor: dotsOverlay
                      ? active
                        ? 'rgba(255,255,255,0.95)'
                        : 'rgba(255,255,255,0.45)'
                      : active
                        ? 'var(--color-accent)'
                        : 'transparent',
                    border: dotsOverlay ? 'none' : '2px solid',
                    borderColor: dotsOverlay
                      ? 'transparent'
                      : active
                        ? 'var(--color-accent)'
                        : 'var(--color-text-muted)',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    transition:
                      'width 180ms ease, background-color 180ms ease, border-color 180ms ease',
                    flexShrink: 0,
                    '&:hover': dotsOverlay
                      ? {
                          bgcolor: active
                            ? '#fff'
                            : 'rgba(255,255,255,0.7)',
                        }
                      : {
                          bgcolor: active
                            ? 'var(--color-accent-strong)'
                            : 'var(--color-accent-soft)',
                          borderColor: active
                            ? 'var(--color-accent-strong)'
                            : 'var(--color-accent)',
                        },
                  }}
                />
              );
            })}
          </Stack>
        )
      )}
    </Box>
  );
}
